import { DEFAULT_MONEY_SCALE, toMinorUnits, type MoneyScale } from '@shared/money';
import type { PosDraftPayment, PosPaymentMethodId, PosPaymentMethodRow } from '../domain/posTypes';
import { toPaymentAmountNumber } from './posCheckoutTotals';

const MERCADOPAGO_QR_MIN_AMOUNT_ARS = 15;
const MERCADOPAGO_QR_MIN_AMOUNT_MESSAGE =
  'Mercado Pago QR pide $15 como mínimo. Subí el monto o elegí otro medio.';

export type NormalizedPaymentDraft = PosDraftPayment & { amountNumber: number };

export function pickDefaultPaymentMethodId(
  methods: PosPaymentMethodRow[],
  lastUsedId: string | null,
  cashSessionOpen: boolean
): PosPaymentMethodRow['id'] {
  const validIds = new Set(methods.map((m) => m.id));
  const lastUsedValid =
    lastUsedId &&
    validIds.has(lastUsedId as PosPaymentMethodRow['id']) &&
    (lastUsedId !== 'cash' || cashSessionOpen);
  const fallbackIds: PosPaymentMethodRow['id'][] = [
    ...(lastUsedValid ? [lastUsedId as PosPaymentMethodRow['id']] : []),
    ...(cashSessionOpen ? ['cash' as const] : []),
    'transfer' as const,
    'mercadopago_qr' as const,
    'fiado' as const,
  ];
  const candidate = fallbackIds.find((id) => validIds.has(id) && (id !== 'cash' || cashSessionOpen));
  return candidate ?? (methods[0]?.id as PosPaymentMethodRow['id'] | undefined) ?? 'transfer';
}

export function normalizePaymentDrafts(
  drafts: PosDraftPayment[],
  scale: MoneyScale = DEFAULT_MONEY_SCALE,
): NormalizedPaymentDraft[] {
  return drafts.map((draft) => ({
    ...draft,
    amountNumber: toPaymentAmountNumber(draft.amount, scale),
  }));
}

export type CheckoutValidationErr = { ok: false; title: string; message: string };
export type CheckoutValidationOk = { ok: true };

export function findUnassignedServiceLines<T extends {
  catalogItemType?: string;
  performedByUserId?: string | null;
}>(cart: T[]): T[] {
  return cart.filter((item) => item.catalogItemType === 'service' && !item.performedByUserId);
}

/** Montos y medios antes de límites customer y totales. */
export function validateCheckoutDraftsBasic(params: {
  normalizedPayments: NormalizedPaymentDraft[];
  mercadopagoQrMethodAllowed: boolean;
  customerMethodAllowed: boolean;
  checkMethodAllowed: boolean;
  activeCashSessionId: string | null;
  saleCustomerId: string | null;
}): CheckoutValidationOk | CheckoutValidationErr {
  const { normalizedPayments, mercadopagoQrMethodAllowed, customerMethodAllowed, checkMethodAllowed, activeCashSessionId, saleCustomerId } = params;

  const rules: Array<{ test: boolean; title: string; message: string }> = [
    {
      test: normalizedPayments.length === 0 || normalizedPayments.some((d) => !d.amountNumber || d.amountNumber <= 0),
      title: 'Cobro incompleto',
      message: 'Poné al menos un medio de pago con un monto válido.',
    },
    {
      test: normalizedPayments.some((d) => d.methodId === 'cash') && !activeCashSessionId,
      title: 'Caja cerrada',
      message: 'Abrí caja antes de cobrar en efectivo.',
    },
    {
      test: normalizedPayments.some((d) => d.methodId === 'mercadopago_qr') && !mercadopagoQrMethodAllowed,
      title: 'Mercado Pago no disponible',
      message: 'Esta integración está apagada por ahora. Elegí otro medio.',
    },
    {
      test: normalizedPayments.some(
        (d) => d.methodId === 'mercadopago_qr' && d.amountNumber < MERCADOPAGO_QR_MIN_AMOUNT_ARS
      ),
      title: 'Monto mínimo Mercado Pago',
      message: MERCADOPAGO_QR_MIN_AMOUNT_MESSAGE,
    },
    {
      test: normalizedPayments.some((d) => d.methodId === 'fiado') && !customerMethodAllowed,
      title: 'Crédito no disponible',
      message: 'Clientes no está en el plan o está apagado. Elegí otro medio.',
    },
    {
      test: normalizedPayments.some((d) => d.methodId === 'fiado' && !d.customerId),
      title: 'Cliente a cuenta incompleto',
      message: 'Elegí a quién se le carga la cuenta.',
    },
    {
      test: normalizedPayments.some((d) => d.methodId === 'cheque') && !checkMethodAllowed,
      title: 'Cheques no disponibles',
      message: 'Los cheques están apagados en Funciones del negocio. Elegí otro medio.',
    },
    {
      test: normalizedPayments.some((d) => d.methodId === 'cheque') && !activeCashSessionId,
      title: 'Caja cerrada',
      message: 'Abrí caja antes de cobrar con cheque.',
    },
    {
      test: normalizedPayments.some((d) => d.methodId === 'cheque') && !saleCustomerId,
      title: 'Cliente requerido',
      message: 'Elegí el cliente que te entregó el cheque (o crealo nuevo arriba).',
    },
    {
      test: normalizedPayments.some((d) => d.methodId === 'cheque' && !d.checkNumber?.trim()),
      title: 'Cheque incompleto',
      message: 'Ingresá el número del cheque.',
    },
  ];

  for (const rule of rules) {
    if (rule.test) return { ok: false, title: rule.title, message: rule.message };
  }
  return { ok: true };
}

/** Total cobrado vs total del carrito (después de validar customer si aplica). */
export function validateCheckoutPaidVersusTotal(params: {
  total: number;
  normalizedPayments: NormalizedPaymentDraft[];
  moneyScale?: MoneyScale;
}): CheckoutValidationOk | CheckoutValidationErr {
  const scale = params.moneyScale ?? DEFAULT_MONEY_SCALE;
  const paidMinor = params.normalizedPayments.reduce(
    (sum, d) => sum + toMinorUnits(d.amountNumber, scale),
    0,
  );
  const totalMinor = toMinorUnits(params.total, scale);
  const hasCashPayment = params.normalizedPayments.some((d) => d.methodId === 'cash');

  if (paidMinor < totalMinor) {
    return {
      ok: false,
      title: 'Cobro incompleto',
      message: 'Lo cobrado no cubre el total.',
    };
  }

  if (paidMinor > totalMinor && !hasCashPayment) {
    return {
      ok: false,
      title: 'Monto excedido',
      message: 'El vuelto solo entra si hay efectivo en el cobro.',
    };
  }

  return { ok: true };
}

export type { CustomerCreditExceededRow } from '../../customers/application/customerCreditLimit';
export { findCustomerCreditExceeded } from '../../customers/application/customerCreditLimit';
